import { Box, Table, TableBody, TableCell, TableRow } from "@mui/material";
import { ContentState, Editor, EditorBlock, EditorState } from "draft-js";
import htmlToDraft from "html-to-draftjs";
import "draft-js/dist/Draft.css";
import { useCallback, useEffect, useState } from "react";

/**
 * Read-only HTML-Viewer auf Basis von Draft.js.
 *
 * Ablauf:
 *   1. normalizeHtml()  - HTML so umschreiben, dass html-to-draftjs damit umgehen kann,
 *                         Tabellen werden dabei ausgelagert und durch Platzhalter ersetzt.
 *   2. htmlToDraft()    - HTML -> Draft-Blöcke
 *   3. applyAlignments()- text-align aus dem HTML nachträglich in die Block-Daten schreiben
 *   4. blockRendererFn  - Sonderblöcke (Tabelle, Bild, ausgerichteter Text) selbst rendern
 */

// Block-Elemente, die eine eigene Textausrichtung tragen können
const ALIGNABLE_SELECTOR = "p, h1, h2, h3, h4, h5, h6, li, blockquote, pre, div";

// ---------------------------------------------------------------------------
// HTML-Normalisierung
// ---------------------------------------------------------------------------

/**
 * Liest eine Tabelle in ein einfaches Datenmodell aus, das TableBlock rendern kann.
 * Draft.js kennt keine verschachtelten Strukturen, deshalb wird die Tabelle
 * komplett aus dem Draft-Content herausgehalten.
 */
function normalizeCell(cell) {
  const clone = cell.cloneNode(true);

  // leere Absätze in der Zelle komplett entfernen
  clone.querySelectorAll("p").forEach((p) => {
    if (!p.textContent.trim() && p.children.length === 0) p.remove();
  });

  // verbleibende Absätze auflösen, mehrere durch <br> trennen
  const paragraphs = Array.from(clone.querySelectorAll("p"));
  paragraphs.forEach((p, index) => {
    if (index > 0) p.parentElement.insertBefore(clone.ownerDocument.createElement("br"), p);
    while (p.firstChild) p.parentElement.insertBefore(p.firstChild, p);
    p.remove();
  });

  return clone.innerHTML;
}

function parseTable(table) {
  const colWidths = Array.from(table.querySelectorAll(":scope > colgroup > col")).map((col) => col.style.minWidth || col.style.width || null);

  const rows = Array.from(table.querySelectorAll("tr"))
    .filter((tr) => tr.closest("table") === table) // verschachtelte Tabellen ignorieren
    .map((tr) =>
      Array.from(tr.children)
        .filter((cell) => cell.tagName === "TD" || cell.tagName === "TH")
        .map((cell) => ({
          header: cell.tagName === "TH",
          colSpan: Number(cell.getAttribute("colspan")) || 1,
          rowSpan: Number(cell.getAttribute("rowspan")) || 1,
          html: normalizeCell(cell),
        })),
    );

  return { colWidths, rows };
}

// Bereitet das Eingabe-HTML für html-to-draftjs vor.
function normalizeHtml(html) {
  const parser = new DOMParser();
  const doc = parser.parseFromString(html, "text/html");
  const tables = [];

  // <li><p>Text</p></li> -> <li>Text</li>
  doc.querySelectorAll("li > p:only-child").forEach((p) => {
    const li = p.parentElement;
    while (p.firstChild) li.insertBefore(p.firstChild, p);
    p.remove();
  });

  // <s> auflößen: nur <del> wird als STRIKETHROUGH erkannt
  doc.querySelectorAll("s, strike").forEach((el) => {
    const del = doc.createElement("del");
    while (el.firstChild) del.appendChild(el.firstChild);
    el.replaceWith(del);
  });

  // Unterstrichen: u -> ins, da nur <ins> als UNDERLINE erkannt wird
  doc.querySelectorAll("u").forEach((el) => {
    const replacement = doc.createElement("ins");
    while (el.firstChild) replacement.appendChild(el.firstChild);
    el.replaceWith(replacement);
  });

  // Tabellen herausziehen und durch Platzhalter ersetzen
  // (Platzhalter = <img src="draft-table://INDEX">, wird zu einem atomic Block)
  doc.querySelectorAll("table").forEach((table) => {
    if (table.parentElement?.closest("table")) return; // ist Teil einer äußeren Tabelle

    const index = tables.push(parseTable(table)) - 1;

    const wrapper = doc.createElement("p");
    const placeholder = doc.createElement("img");
    placeholder.setAttribute("src", `draft-table://${index}`);
    placeholder.setAttribute("alt", `Tabelle ${index + 1}`);
    wrapper.appendChild(placeholder);

    table.replaceWith(wrapper);
  });

  // Nur echte technische Leerabsätze entfernen
  doc.querySelectorAll("p").forEach((p) => {
    const hasContent = p.textContent.replace(/[\u200C\u00A0\s]/g, "").length > 0;
    const hasElements = p.children.length > 0;

    if (!hasContent && !hasElements) {
      const previous = p.previousElementSibling;
      if (previous?.tagName === "P" && previous.textContent.trim() === "") p.remove();
    }
  });

  return { html: doc.body.innerHTML, tables };
}

// ---------------------------------------------------------------------------
// Textausrichtung
// html-to-draftjs überträgt text-align nicht, deshalb wird die Ausrichtung
// separat aus dem HTML gelesen und nachträglich in die Block-Daten geschrieben.
// ---------------------------------------------------------------------------

/**
 * Ermittelt die effektive Ausrichtung eines Elements. Läuft die Elternkette
 * hoch, bis eine Ausrichtung gesetzt ist; "left" gilt als Default (= null).
 */
function readAlign(el) {
  let current = el;
  while (current && current.style) {
    const align = current.style.textAlign;
    if (align === "center" || align === "right" || align === "justify") return align;
    if (align === "left") return null;
    current = current.parentElement;
  }
  return null;
}

/**
 * Sammelt für jeden innersten Block im HTML dessen Text und Ausrichtung.
 * Der Text dient später als Anker für die Zuordnung zu den Draft-Blöcken.
 */
function collectAlignments(html) {
  const doc = new DOMParser().parseFromString(html, "text/html");

  return Array.from(doc.querySelectorAll(ALIGNABLE_SELECTOR))
    .filter((el) => !el.querySelector(ALIGNABLE_SELECTOR)) // nur innerste Blöcke
    .map((el) => ({
      text: el.textContent.replace(/\s+/g, " ").trim(),
      align: readAlign(el),
    }));
}

/**
 * Schreibt die gesammelten Ausrichtungen in die Block-Daten.
 * Die Zuordnung läuft über Textabgleich mit nur vorwärts laufendem Cursor,
 * damit entfernte Absätze oder Tabellen-Platzhalter die Reihenfolge nicht zerstören.
 */
function applyAlignments(contentBlocks, alignments) {
  let cursor = 0;

  return contentBlocks.map((block) => {
    if (block.getType() === "atomic") return block;

    const text = block.getText().replace(/\s+/g, " ").trim();

    let match = -1;
    for (let i = cursor; i < alignments.length; i++) {
      if (alignments[i].text === text) {
        match = i;
        break;
      }
    }
    if (match === -1) return block; // kein Treffer -> Default, Cursor bleibt stehen

    cursor = match + 1;
    const align = alignments[match].align;

    return align ? block.set("data", block.getData().set("textAlign", align)) : block;
  });
}

// ---------------------------------------------------------------------------
// Block-Komponenten
// Sonderblöcke, die Draft.js nicht selbst darstellen kann
// ---------------------------------------------------------------------------

/** Rendert eine über normalizeHtml ausgelagerte Tabelle als MUI-Table. */
const TableBlock = ({ table }) => {
  if (!table) return null;

  return (
    <Table size="small" sx={{ my: 1, width: "100%", tableLayout: "fixed" }}>
      {table.colWidths.length > 0 && (
        <colgroup>
          {table.colWidths.map((width, i) => (
            <col key={i} style={width ? { minWidth: width } : undefined} />
          ))}
        </colgroup>
      )}
      <TableBody>
        {table.rows.map((cells, rowIndex) => (
          <TableRow key={rowIndex}>
            {cells.map((cell, cellIndex) => (
              <TableCell
                key={cellIndex}
                component={cell.header ? "th" : "td"}
                variant={cell.header ? "head" : "body"}
                colSpan={cell.colSpan}
                rowSpan={cell.rowSpan}
                sx={{
                  border: 1,
                  borderColor: "divider",
                  verticalAlign: "top",
                  bgcolor: cell.header ? "action.hover" : "transparent",
                  "& p": { m: 0 },
                  "& p + p": { mt: 1 },
                }}
                dangerouslySetInnerHTML={{ __html: cell.html }}
              />
            ))}
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );
};

/** Rendert ein Bild aus einer IMAGE-Entity. */
const ImageBlock = ({ contentState, block }) => {
  const entityKey = block.getEntityAt(0);
  if (!entityKey) return null;

  const { src, alt, width, height, alignment } = contentState.getEntity(entityKey).getData();

  return (
    <img
      src={src}
      alt={alt || ""}
      style={{
        width: width || "auto",
        height: height || "auto",
        float: alignment || "none",
        maxWidth: "100%",
      }}
    />
  );
};

/**
 * Textblock mit Ausrichtung. EditorBlock übernimmt weiterhin den Inhalt,
 * damit Inline-Formatierung und Entities erhalten bleiben. Der Selektor auf
 * [data-offset-key] ist nötig, weil Draft.css am inneren Element text-align setzt.
 */
const TextBlock = (props) => {
  const align = props.block.getData().get("textAlign");
  if (!align) return <EditorBlock {...props} />;

  return (
    <Box sx={{ "& > [data-offset-key]": { textAlign: align } }}>
      <EditorBlock {...props} />
    </Box>
  );
};

/** Verteilt atomic Blöcke auf Tabelle oder Bild anhand der Entity-src. */
const AtomicBlock = (props) => {
  const entityKey = props.block.getEntityAt(0);
  if (!entityKey) return null;

  const entity = props.contentState.getEntity(entityKey);
  if (entity.getType() !== "IMAGE") return null;

  const data = entity.getData();
  const match = /^draft-table:\/\/(\d+)$/.exec(data.src || "");

  // Platzhalter aus normalizeHtml -> ausgelagerte Tabelle
  if (match) {
    const tables = props.blockProps?.tables || [];
    return <TableBlock table={tables[Number(match[1])]} />;
  }

  return <ImageBlock {...props} />;
};

// ---------------------------------------------------------------------------
// Editor
// ---------------------------------------------------------------------------

const HtmlEditor = ({ html }) => {
  const [editorState, setEditorState] = useState(() => EditorState.createEmpty());
  const [tables, setTables] = useState([]);

  // HTML -> EditorState, bei jeder Änderung der Quelle neu aufbauen
  useEffect(() => {
    if (!html) return;

    const { html: normalizedHtml, tables: parsedTables } = normalizeHtml(html);
    const contentBlock = htmlToDraft(normalizedHtml);

    setTables(parsedTables);

    if (contentBlock) {
      const alignments = collectAlignments(normalizedHtml);
      const contentState = ContentState.createFromBlockArray(
        applyAlignments(contentBlock.contentBlocks, alignments),
        contentBlock.entityMap,
      );
      setEditorState(EditorState.createWithContent(contentState));
    }
  }, [html]);

  // Nur Sonderfälle übernehmen, alles andere rendert Draft.js selbst
  const blockRendererFn = useCallback(
    (block) => {
      if (block.getType() === "atomic") {
        return { component: AtomicBlock, editable: false, props: { tables } };
      }
      if (block.getData().get("textAlign")) {
        return { component: TextBlock, editable: false };
      }
      return null;
    },
    [tables],
  );

  return (
    <Box sx={{ color: "text.primary" }}>
      <Editor
        editorState={editorState}
        onChange={setEditorState}
        blockRendererFn={blockRendererFn}
        readOnly
      />
    </Box>
  );
};

export { HtmlEditor };